<template>
    <defaultSec title="旅程执行看板" :returnState="true" :isDisableScroll="true">
        <section class="sop-dashboard">
            <div class="filter-bar">
                <el-form :inline="true" size="mini" class="select-wrap">
                    <el-form-item label="旅程">
                        <el-select v-model="filters.routeId" filterable placeholder="选择旅程" style="width: 230px" @change="onRouteChange">
                            <el-option v-for="item in routeOptions" :key="item.routeId" :label="item.routeName" :value="item.routeId">
                                <span>{{ item.routeName }}</span>
                                <span class="option-extra">{{ item.statusLabel }}</span>
                            </el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item label="日期">
                        <el-date-picker v-model="filters.date" type="date" format="yyyy-MM-dd" value-format="yyyy-MM-dd"
                                        :clearable="false" style="width: 135px" @change="loadDashboard"></el-date-picker>
                    </el-form-item>
                    <el-form-item label="企微">
                        <el-select v-model="filters.qyWeixinUserid" filterable clearable placeholder="全部企微"
                                   style="width: 180px" @change="loadDashboard">
                            <el-option v-for="item in qwUsers" :key="item.id" :label="item.name" :value="item.id"></el-option>
                            <el-option label="未分配企微" :value="unassignedQy"></el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item label="批次">
                        <el-select v-model="filters.routeRecordId" clearable placeholder="全部批次" style="width: 210px" @change="loadDashboard">
                            <el-option v-for="item in batchOptions" :key="item.routeRecordId"
                                       :label="batchLabel(item)" :value="item.routeRecordId"></el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item>
                        <el-button type="primary" @click="loadDashboard">查询</el-button>
                        <el-button icon="el-icon-refresh" :loading="loading" @click="refresh">刷新</el-button>
                    </el-form-item>
                </el-form>
            </div>

            <div class="dashboard-scroll">
                <el-alert v-if="loadError" type="error" :closable="false" show-icon title="看板加载失败">
                    <div class="alert-content">
                        <span>{{ loadError }}</span>
                        <el-button size="mini" type="primary" plain @click="loadDashboard">重试</el-button>
                    </div>
                </el-alert>

                <div v-else v-loading="loading" class="dashboard-content">
                    <template v-if="dashboard">
                        <div class="context-card">
                            <div class="context-main">
                                <span class="context-route">{{ context.routeName || '未知旅程' }}</span>
                                <el-tag size="mini" :type="context.routeStatus === 1 ? 'success' : (context.routeStatus === 2 ? 'info' : 'warning')">
                                    {{ context.routeStatusLabel }}
                                </el-tag>
                                <span class="context-date">{{ context.date }}</span>
                                <span v-if="filters.qyWeixinUserid" class="context-tag">企微：{{ qyFilterLabel }}</span>
                                <span v-if="filters.routeRecordId" class="context-tag">批次：{{ shortBatchId(filters.routeRecordId) }}</span>
                            </div>
                            <div class="context-meta">
                                <span>节点数：{{ context.sopNodeCount }}</span>
                                <span>当日执行：{{ context.runCount }} 次（成功 {{ context.successRuns }} / 失败 {{ context.failedRuns }}）</span>
                                <span>任务来源批次：{{ context.taskBatchCount }}</span>
                                <span>最近执行：{{ context.lastRunTime || '无当日执行记录' }}</span>
                                <span>数据更新：{{ context.dataUpdatedAt || '无任务数据' }}</span>
                                <span>查询时间：{{ dashboard.queriedAt }}</span>
                                <span v-if="context.entryWindowStart">表内任务业务日期：{{ context.entryWindowStart }} 至 {{ context.entryWindowEnd }}</span>
                            </div>
                        </div>

                        <div class="section-title">用户概况 <span class="section-hint">去重人数；进入按批次日期、任务按业务日期，二者不要求相等</span></div>
                        <div class="kpi-grid user-kpi">
                            <div v-for="card in userCards" :key="card.key" class="kpi-card" :class="card.tone" @click="openUsers(card.scope)">
                                <div class="kpi-label">{{ card.label }}</div>
                                <div class="kpi-value">{{ formatNumber(card.value) }}</div>
                                <div class="kpi-hint">{{ card.hint }}</div>
                            </div>
                        </div>

                        <div class="section-title">任务概况 <span class="section-hint">按去重后的任务实例统计，点击查看同口径任务</span></div>
                        <div class="kpi-grid task-kpi">
                            <div v-for="card in taskCards" :key="card.key" class="kpi-card" :class="card.tone" @click="openTasks(card.filter)">
                                <div class="kpi-label">{{ card.label }}</div>
                                <div class="kpi-value">{{ formatNumber(card.value) }}</div>
                                <div class="kpi-hint">{{ card.hint }}</div>
                            </div>
                        </div>

                        <el-alert v-if="dataQuality.statusReconciled === false" class="empty-notice" type="error" :closable="false"
                                  show-icon title="状态合计与任务总数不一致" description="汇总未通过自校验，请勿直接使用当前数字，先检查后台状态值。"></el-alert>
                        <el-alert v-if="taskOverview.total === 0" class="empty-notice" :type="context.runCount ? 'info' : 'warning'"
                                  :closable="false" show-icon :title="emptyTaskTitle" :description="emptyTaskDescription"></el-alert>
                        <el-alert v-if="dataQuality.orphanBatchRows > 0" class="empty-notice" type="warning" :closable="false"
                                  show-icon :title="'有 ' + dataQuality.orphanBatchRows + ' 条进入记录未关联旅程批次'"
                                  description="这些记录仍计入用户/任务统计，未从汇总中遗漏；批次明细无法定位到具体执行批次。"></el-alert>

                        <div class="panel-grid">
                            <div class="panel">
                                <div class="panel-head">
                                    <div class="panel-title">企微汇总 <span class="section-hint">共 {{ filteredWecom.length }} 个企微</span></div>
                                    <div class="panel-actions">
                                        <el-input v-model="wecomSearch" size="mini" clearable placeholder="搜索企微账号" style="width: 160px"></el-input>
                                        <el-checkbox v-model="onlyIncomplete" size="mini">仅看未完成</el-checkbox>
                                        <el-checkbox v-model="onlyInvalid" size="mini">仅看无效</el-checkbox>
                                    </div>
                                </div>
                                <el-table :data="filteredWecom" size="mini" border class="clickable-table" empty-text="当前筛选无企微任务" @row-click="openWecomTasks">
                                    <el-table-column label="企微账号" min-width="150">
                                        <template slot-scope="scope">
                                            <div class="wecom-name">{{ scope.row.qyWeixinUserName }}</div>
                                            <div class="wecom-id">{{ scope.row.unassigned ? '后台未记录企微账号' : scope.row.qyWeixinUseridRaw }}</div>
                                        </template>
                                    </el-table-column>
                                    <el-table-column label="任务" width="70" align="right">
                                        <template slot-scope="scope"><span class="cell-link" @click.stop="openTasks({ qyWeixinUserid: scope.row.qyWeixinUserid })">{{ formatNumber(scope.row.total) }}</span></template>
                                    </el-table-column>
                                    <el-table-column label="有效" width="70" align="right">
                                        <template slot-scope="scope"><span class="cell-link" @click.stop="openTasks({ qyWeixinUserid: scope.row.qyWeixinUserid, statusGroup: 'valid' })">{{ formatNumber(scope.row.valid) }}</span></template>
                                    </el-table-column>
                                    <el-table-column label="无效" width="70" align="right">
                                        <template slot-scope="scope"><span class="cell-link danger" @click.stop="openTasks({ qyWeixinUserid: scope.row.qyWeixinUserid, statusGroup: 'invalid' })">{{ formatNumber(scope.row.invalid) }}</span></template>
                                    </el-table-column>
                                    <el-table-column label="待确认" width="75" align="right">
                                        <template slot-scope="scope"><span class="cell-link" @click.stop="openTasks({ qyWeixinUserid: scope.row.qyWeixinUserid, statusGroup: 'pending' })">{{ formatNumber(scope.row.pendingConfirm) }}</span></template>
                                    </el-table-column>
                                    <el-table-column label="待处理" width="70" align="right">
                                        <template slot-scope="scope"><span class="cell-link" @click.stop="openTasks({ qyWeixinUserid: scope.row.qyWeixinUserid, status: 0 })">{{ formatNumber(scope.row.pending) }}</span></template>
                                    </el-table-column>
                                    <el-table-column label="已处理" width="70" align="right">
                                        <template slot-scope="scope"><span class="cell-link" @click.stop="openTasks({ qyWeixinUserid: scope.row.qyWeixinUserid, status: 1 })">{{ formatNumber(scope.row.handled) }}</span></template>
                                    </el-table-column>
                                    <el-table-column label="已发送" width="70" align="right">
                                        <template slot-scope="scope"><span class="cell-link" @click.stop="openTasks({ qyWeixinUserid: scope.row.qyWeixinUserid, status: 2 })">{{ formatNumber(scope.row.sent) }}</span></template>
                                    </el-table-column>
                                    <el-table-column label="已回复" width="70" align="right">
                                        <template slot-scope="scope"><span class="cell-link" @click.stop="openTasks({ qyWeixinUserid: scope.row.qyWeixinUserid, status: 3 })">{{ formatNumber(scope.row.replied) }}</span></template>
                                    </el-table-column>
                                    <el-table-column label="未完成" width="75" align="right">
                                        <template slot-scope="scope"><span class="cell-link" @click.stop="openTasks({ qyWeixinUserid: scope.row.qyWeixinUserid, statuses: [0, 1, -1] })">{{ formatNumber(scope.row.openCount) }}</span></template>
                                    </el-table-column>
                                </el-table>
                            </div>

                            <div class="panel">
                                <div class="panel-head">
                                    <div class="panel-title">无效原因分布 <span class="section-hint">按去重后任务的主原因</span></div>
                                </div>
                                <el-table :data="invalidReasons" size="mini" border class="clickable-table" empty-text="当天没有无效任务" @row-click="openReasonTasks">
                                    <el-table-column label="原因" min-width="180">
                                        <template slot-scope="scope">
                                            <span :class="scope.row.unknown ? 'unknown-reason' : ''">{{ scope.row.reasonLabel }}</span>
                                            <div v-if="scope.row.unknown" class="reason-note">后台原因为空，未推断具体规则</div>
                                        </template>
                                    </el-table-column>
                                    <el-table-column label="任务" width="70" align="right">
                                        <template slot-scope="scope">{{ formatNumber(scope.row.taskCount) }}</template>
                                    </el-table-column>
                                    <el-table-column label="用户" width="70" align="right">
                                        <template slot-scope="scope">{{ formatNumber(scope.row.userCount) }}</template>
                                    </el-table-column>
                                    <el-table-column label="占无效" width="75" align="right">
                                        <template slot-scope="scope">{{ formatPercent(scope.row.share) }}</template>
                                    </el-table-column>
                                </el-table>
                                <div class="panel-footnote">
                                    有效 {{ formatNumber(taskOverview.valid) }}，无效 {{ formatNumber(taskOverview.invalid) }}，
                                    待系统确认 {{ formatNumber(taskOverview.pendingConfirm) }}，未知状态 {{ formatNumber(taskOverview.unknown) }}。
                                    已处理 {{ formatNumber(taskOverview.handledOnly) }} 仅代表点击发送按钮；有发送证据（已发送+已回复）
                                    {{ formatNumber(taskOverview.sentEvidence) }}。
                                </div>
                                <div class="panel-footnote" v-if="dataQuality.mergedDuplicateRows > 0">
                                    原始任务记录 {{ formatNumber(dataQuality.rawTaskRows) }} 条，合并重复行
                                    {{ formatNumber(dataQuality.mergedDuplicateRows) }} 条，去重后 {{ formatNumber(dataQuality.logicalTasks) }} 条。
                                </div>
                            </div>
                        </div>
                    </template>
                    <el-empty v-else-if="!loading" description="暂无看板数据"></el-empty>
                </div>
            </div>

            <el-drawer :visible.sync="taskDrawer" :with-header="false" size="82%" @opened="bindTaskScroll" @close="onTaskDrawerClosed">
                <div class="drawer-wrap">
                    <div class="drawer-head">
                        <div>
                            <span class="drawer-title">任务明细</span>
                            <span class="section-hint">{{ taskContextLabel }}</span>
                        </div>
                        <el-button size="mini" icon="el-icon-close" @click="taskDrawer = false">关闭</el-button>
                    </div>
                    <div class="drawer-filter">
                        <el-select v-model="taskFilters.statusGroup" size="mini" clearable placeholder="有效性" style="width: 130px" @change="searchTasks">
                            <el-option label="有效任务" value="valid"></el-option>
                            <el-option label="无效任务" value="invalid"></el-option>
                            <el-option label="待系统确认" value="pending"></el-option>
                            <el-option label="未知状态" value="unknown"></el-option>
                        </el-select>
                        <el-select v-model="taskFilters.status" size="mini" clearable placeholder="具体状态" style="width: 150px" @change="searchTasks">
                            <el-option v-for="item in taskStatusOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
                        </el-select>
                        <el-input v-model="taskFilters.keyword" size="mini" clearable placeholder="昵称/手机号/客户ID/企微" style="width: 220px"
                                  @keyup.enter.native="searchTasks"></el-input>
                        <el-button size="mini" type="primary" @click="searchTasks">查询</el-button>
                        <el-button size="mini" @click="resetTaskFilters">重置</el-button>
                        <el-tag v-if="taskFilters.qyWeixinUserid" size="mini" closable @close="clearTaskQy">{{ taskQyLabel }}</el-tag>
                        <el-tag v-if="taskFilters.statuses && taskFilters.statuses.length" size="mini" type="warning" closable @close="clearTaskStatuses">{{ taskStatusesLabel }}</el-tag>
                        <el-tag v-if="taskFilters.reason" size="mini" type="danger" closable @close="clearTaskReason">{{ taskReasonLabel }}</el-tag>
                        <el-tag v-if="taskFilters.routeRecordId" size="mini" closable @close="clearTaskBatch">批次 {{ shortBatchId(taskFilters.routeRecordId) }}</el-tag>
                    </div>
                    <el-alert v-if="taskError" type="error" :closable="false" show-icon :title="taskError"></el-alert>
                    <div class="drawer-table" v-loading="taskLoading">
                        <el-table ref="taskTable" :data="taskList" size="mini" border height="calc(100vh - 245px)" empty-text="当前筛选无任务"
                                  @cell-mouse-enter="rememberTaskScroll"
                                  @row-click="openTaskDetail">
                            <el-table-column label="用户" min-width="165">
                                <template slot-scope="scope">
                                    <div class="strong">{{ scope.row.nickName }}</div>
                                    <div class="muted">{{ scope.row.phone || scope.row.customerId }}</div>
                                </template>
                            </el-table-column>
                            <el-table-column label="企微" min-width="120">
                                <template slot-scope="scope">
                                    <div>{{ scope.row.qyWeixinUserName }}</div>
                                    <div class="muted">{{ scope.row.qyWeixinUserid || '未分配' }}</div>
                                </template>
                            </el-table-column>
                            <el-table-column label="业务日期/批次" width="155">
                                <template slot-scope="scope">
                                    <div>{{ scope.row.createDate }}</div>
                                    <div class="muted">{{ scope.row.batchDate || shortBatchId(scope.row.routeRecordId) }}</div>
                                </template>
                            </el-table-column>
                            <el-table-column label="SOP节点/素材" min-width="160">
                                <template slot-scope="scope">
                                    <div>{{ scope.row.materialName || scope.row.materialCode }}</div>
                                    <div class="muted">{{ scope.row.materialCode }} {{ scope.row.materialParentCode ? ' / 前置 ' + scope.row.materialParentCode : '' }}</div>
                                </template>
                            </el-table-column>
                            <el-table-column label="状态" width="145">
                                <template slot-scope="scope">
                                    <el-tag size="mini" :type="statusTagType(scope.row.statusCode)">{{ scope.row.statusLabel }}</el-tag>
                                    <div v-if="scope.row.duplicateRows > 1" class="muted">合并 {{ scope.row.duplicateRows }} 条重复记录</div>
                                </template>
                            </el-table-column>
                            <el-table-column label="原因" min-width="170">
                                <template slot-scope="scope">
                                    <div class="status-reason">{{ scope.row.invalid ? scope.row.reasonLabel : '—' }}</div>
                                </template>
                            </el-table-column>
                            <el-table-column label="生成/更新时间" width="150">
                                <template slot-scope="scope">
                                    <div>{{ scope.row.createTime }}</div>
                                    <div class="muted">{{ scope.row.kefuUseTime || '无处理时间' }}</div>
                                </template>
                            </el-table-column>
                            <el-table-column label="操作" width="70">
                                <template slot-scope="scope">
                                    <el-button type="text" size="mini" @click.stop="openTaskDetail(scope.row)">详情</el-button>
                                </template>
                            </el-table-column>
                        </el-table>
                        <tablePagination :total="taskTotal" :pageSize="taskPageSize" :currentPage="taskPage" @handlePage="handleTaskPage"></tablePagination>
                    </div>
                </div>
            </el-drawer>

            <el-drawer :visible.sync="userDrawer" :with-header="false" size="72%" @close="restoreDashboardScroll">
                <div class="drawer-wrap">
                    <div class="drawer-head">
                        <div>
                            <span class="drawer-title">{{ userDrawerTitle }}</span>
                            <span class="section-hint">{{ userContextLabel }}</span>
                        </div>
                        <el-button size="mini" icon="el-icon-close" @click="userDrawer = false">关闭</el-button>
                    </div>
                    <div class="drawer-filter">
                        <el-radio-group v-model="userScope" size="mini" @change="onUserScopeChange">
                            <el-radio-button label="entered">当日进入</el-radio-button>
                            <el-radio-button label="task">当日有任务</el-radio-button>
                            <el-radio-button label="noTask">未生成任务</el-radio-button>
                            <el-radio-button label="all">累计进入</el-radio-button>
                        </el-radio-group>
                        <el-input v-model="userKeyword" size="mini" clearable placeholder="昵称/手机号/客户ID/企微" style="width: 220px"
                                  @keyup.enter.native="searchUsers"></el-input>
                        <el-button size="mini" type="primary" @click="searchUsers">查询</el-button>
                    </div>
                    <el-alert v-if="userError" type="error" :closable="false" show-icon :title="userError"></el-alert>
                    <div class="drawer-table" v-loading="userLoading">
                        <el-table :data="userList" size="mini" border height="calc(100vh - 245px)" empty-text="当前筛选无用户">
                            <el-table-column label="用户" min-width="200">
                                <template slot-scope="scope">
                                    <div class="strong">{{ scope.row.nickName }}</div>
                                    <div class="muted">{{ scope.row.phone || scope.row.customerId }}</div>
                                </template>
                            </el-table-column>
                            <el-table-column label="企微" min-width="130">
                                <template slot-scope="scope">
                                    <div>{{ scope.row.qyWeixinUserName }}</div>
                                    <div class="muted">{{ scope.row.qyWeixinUserid || '未分配' }}</div>
                                </template>
                            </el-table-column>
                            <el-table-column label="进入/最近时间" width="165">
                                <template slot-scope="scope">
                                    <div>{{ scope.row.enteredAt || '—' }}</div>
                                    <div class="muted">{{ scope.row.lastUseTime || '' }}</div>
                                </template>
                            </el-table-column>
                            <el-table-column label="任务数" width="85" align="right">
                                <template slot-scope="scope">
                                    <span class="cell-link" @click.stop="openUserTasks(scope.row)">{{ formatNumber(scope.row.taskCount) }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column v-if="userScope === 'task'" label="待处理/已处理/已发送/已回复" min-width="210">
                                <template slot-scope="scope">
                                    <span class="status-count">{{ scope.row.pending }}</span> /
                                    <span class="status-count">{{ scope.row.handled }}</span> /
                                    <span class="status-count">{{ scope.row.sent }}</span> /
                                    <span class="status-count">{{ scope.row.replied }}</span>
                                </template>
                            </el-table-column>
                            <el-table-column v-if="userScope === 'task'" label="无效/待确认" width="110" align="right">
                                <template slot-scope="scope">{{ scope.row.invalid }} / {{ scope.row.pendingConfirm }}</template>
                            </el-table-column>
                            <el-table-column v-if="userScope === 'entered' || userScope === 'all'" label="是否生成任务" width="110">
                                <template slot-scope="scope">
                                    <el-tag size="mini" :type="scope.row.hasTask ? 'success' : 'info'">{{ scope.row.hasTask ? '已生成' : '未生成' }}</el-tag>
                                </template>
                            </el-table-column>
                            <el-table-column label="操作" width="80">
                                <template slot-scope="scope">
                                    <el-button type="text" size="mini" @click.stop="openUserTasks(scope.row)">看任务</el-button>
                                </template>
                            </el-table-column>
                        </el-table>
                        <tablePagination :total="userTotal" :pageSize="userPageSize" :currentPage="userPage" @handlePage="handleUserPage"></tablePagination>
                    </div>
                </div>
            </el-drawer>

            <el-drawer :visible.sync="detailDrawer" title="任务详情与日志" size="62%" @close="invalidateDetailRequest" @closed="restoreTaskScroll">
                <div v-loading="detailLoading" class="detail-wrap">
                    <el-alert v-if="detailError" type="error" :closable="false" show-icon :title="detailError"></el-alert>
                    <template v-if="detail">
                        <div class="detail-grid">
                            <div><span class="detail-label">用户</span>{{ detail.task.nickName }}<span class="muted"> / {{ detail.task.phone || detail.task.customerId }}</span></div>
                            <div><span class="detail-label">企微</span>{{ detail.task.qyWeixinUserName }}<span class="muted"> / {{ detail.task.qyWeixinUserid || '未分配' }}</span></div>
                            <div><span class="detail-label">状态</span><el-tag size="mini" :type="statusTagType(detail.task.statusCode)">{{ detail.task.statusLabel }}</el-tag></div>
                            <div><span class="detail-label">业务日期</span>{{ detail.task.createDate }}<span class="muted">（计划归属）</span></div>
                            <div><span class="detail-label">批次</span>{{ detail.batch ? detail.batch.executeTime + ' / ' + detail.batch.execStatusLabel : shortBatchId(detail.task.routeRecordId) }}</div>
                            <div><span class="detail-label">素材节点</span>{{ detail.task.materialName || detail.task.materialCode }} <span class="muted">{{ detail.task.materialCode }}</span></div>
                        </div>
                        <div v-if="detail.reasons.length" class="reason-list">
                            <span class="detail-label">全部原因（含重复行）</span>
                            <el-tag v-for="reason in detail.reasons" :key="reason" size="mini" type="danger" class="reason-tag">{{ reason }}</el-tag>
                        </div>
                        <div v-else class="reason-list">
                            <span class="detail-label">全部原因（含重复行）</span><span class="muted">无记录原因</span>
                        </div>
                        <div class="detail-section-title">可追溯环节</div>
                        <el-timeline class="detail-timeline">
                            <el-timeline-item v-for="item in detail.timeline" :key="item.key" :color="timelineColor(item.state)"
                                              :timestamp="item.time || '无时间记录'">
                                <div class="timeline-title">{{ item.label }}
                                    <el-tag size="mini" :type="timelineTagType(item.state)">{{ timelineStateLabel(item.state) }}</el-tag>
                                </div>
                                <div class="timeline-detail">{{ item.detail }}</div>
                                <div class="timeline-evidence">证据：{{ item.evidence }}</div>
                            </el-timeline-item>
                        </el-timeline>
                        <div class="detail-section-title">任务记录（{{ detail.instances.length }} 条，含重复/重试行）</div>
                        <el-table :data="detail.instances" size="mini" border max-height="220">
                            <el-table-column prop="id" label="ID" width="85"></el-table-column>
                            <el-table-column label="状态" width="150">
                                <template slot-scope="scope"><el-tag size="mini" :type="statusTagType(scope.row.statusCode)">{{ scope.row.statusLabel }}</el-tag></template>
                            </el-table-column>
                            <el-table-column prop="reasonLabel" label="原因" min-width="140"></el-table-column>
                            <el-table-column prop="remark" label="备注" min-width="140"></el-table-column>
                            <el-table-column prop="createTime" label="生成时间" width="145"></el-table-column>
                            <el-table-column prop="kefuUseTime" label="处理时间" width="145"></el-table-column>
                        </el-table>
                        <div class="detail-section-title">日志</div>
                        <el-table :data="detail.logs" size="mini" border max-height="180" empty-text="无日志记录">
                            <el-table-column prop="createDate" label="日期" width="100"></el-table-column>
                            <el-table-column prop="statusLabel" label="状态" width="180"></el-table-column>
                            <el-table-column prop="reason" label="原因" min-width="140"></el-table-column>
                            <el-table-column prop="remark" label="日志" min-width="180"></el-table-column>
                        </el-table>
                        <div class="panel-footnote">{{ (detail.dataSources || []).join('；') }}</div>
                    </template>
                </div>
            </el-drawer>
        </section>
    </defaultSec>
</template>

<script>
import defaultSec from '@c/defaultSection'
import tablePagination from '@c/tablePagination'

export default {
    name: 'routeSopDashboard',
    components: { defaultSec, tablePagination },
    data() {
        return {
            unassignedQy: '__UNASSIGNED__',
            filters: {
                routeId: null,
                routeName: '',
                date: new Date().Format('yyyy-MM-dd'),
                qyWeixinUserid: '',
                routeRecordId: ''
            },
            routeOptions: [],
            qwUsers: [],
            loading: false,
            loadError: '',
            dashboard: null,
            savedDashboardScroll: 0,
            savedTaskScroll: 0,
            dashboardSeq: 0,
            taskSeq: 0,
            userSeq: 0,
            detailSeq: 0,
            taskScrollElements: [],
            wecomSearch: '',
            onlyIncomplete: false,
            onlyInvalid: false,
            taskDrawer: false,
            taskLoading: false,
            taskError: '',
            taskFilters: {
                statusGroup: '',
                status: null,
                reason: '',
                keyword: '',
                qyWeixinUserid: '',
                routeRecordId: ''
            },
            taskList: [],
            taskTotal: 0,
            taskPage: 1,
            taskPageSize: 20,
            userDrawer: false,
            userLoading: false,
            userError: '',
            userScope: 'entered',
            userKeyword: '',
            userList: [],
            userTotal: 0,
            userPage: 1,
            userPageSize: 20,
            detailDrawer: false,
            detailLoading: false,
            detailError: '',
            detail: null,
            taskStatusOptions: [
                { value: 0, label: '待处理' },
                { value: 1, label: '已处理（未确认发送）' },
                { value: 2, label: '已发送' },
                { value: 3, label: '已回复' },
                { value: -1, label: '待系统确认' },
                { value: -2, label: '已过滤' },
                { value: -3, label: '禁用' }
            ]
        }
    },
    computed: {
        context() {
            return (this.dashboard && this.dashboard.context) || {}
        },
        userOverview() {
            return (this.dashboard && this.dashboard.userOverview) || {}
        },
        taskOverview() {
            return (this.dashboard && this.dashboard.taskOverview) || {}
        },
        dataQuality() {
            return (this.dashboard && this.dashboard.dataQuality) || {}
        },
        wecomSummary() {
            return (this.dashboard && this.dashboard.wecomSummary) || []
        },
        invalidReasons() {
            return (this.dashboard && this.dashboard.invalidReasons) || []
        },
        batchOptions() {
            return (this.dashboard && this.dashboard.batches) || []
        },
        filteredWecom() {
            const keyword = (this.wecomSearch || '').trim().toLowerCase()
            return this.wecomSummary.filter(item => {
                if (keyword && String(item.qyWeixinUserName || '').toLowerCase().indexOf(keyword) < 0
                    && String(item.qyWeixinUseridRaw || '').toLowerCase().indexOf(keyword) < 0) {
                    return false
                }
                if (this.onlyIncomplete && !(item.openCount > 0)) {
                    return false
                }
                if (this.onlyInvalid && !(item.invalid > 0)) {
                    return false
                }
                return true
            })
        },
        qyFilterLabel() {
            return this.filters.qyWeixinUserid === this.unassignedQy ? '未分配企微' : this.filters.qyWeixinUserid
        },
        userCards() {
            return [
                { key: 'total', label: '表内累计进入用户', value: this.userOverview.totalEnteredUsers, scope: 'all', tone: 'tone-blue', hint: '按客户ID去重，不含群组人数' },
                { key: 'entered', label: '当日进入用户', value: this.userOverview.enteredUsers, scope: 'entered', tone: 'tone-green', hint: '所选日期执行批次的进入记录去重' },
                { key: 'taskUsers', label: '当日有任务用户', value: this.userOverview.taskUsers, scope: 'task', tone: 'tone-orange', hint: '按业务日期统计的去重任务用户' },
                { key: 'noTask', label: '当日进入未生成任务', value: this.userOverview.enteredNoTaskUsers, scope: 'noTask', tone: 'tone-gray', hint: '进入批次内没有素材/任务节点记录' }
            ]
        },
        taskCards() {
            const total = this.taskOverview
            return [
                { key: 'total', label: '当日任务', value: total.total, filter: { statusGroup: '' }, tone: 'tone-blue', hint: '去重任务实例；点击查看全部' },
                { key: 'valid', label: '有效任务', value: total.valid, filter: { statusGroup: 'valid' }, tone: 'tone-green', hint: '状态 0/1/2/3' },
                { key: 'invalid', label: '无效任务', value: total.invalid, filter: { statusGroup: 'invalid' }, tone: 'tone-red', hint: '状态 -2 已过滤 / -3 禁用' },
                { key: 'pendingConfirm', label: '待系统确认', value: total.pendingConfirm, filter: { statusGroup: 'pending' }, tone: 'tone-orange', hint: '状态 -1，尚未归入有效/无效' },
                { key: 'pending', label: '待处理', value: total.pending, filter: { status: 0 }, tone: 'tone-orange', hint: '状态 0，未点击发送' },
                { key: 'handled', label: '已处理', value: total.handled, filter: { status: 1 }, tone: 'tone-gray', hint: '状态 1，仅点击发送按钮' },
                { key: 'sent', label: '已发送', value: total.sent, filter: { status: 2 }, tone: 'tone-green', hint: '状态 2，有聊天记录发送证据' },
                { key: 'replied', label: '已回复', value: total.replied, filter: { status: 3 }, tone: 'tone-green', hint: '状态 3，回复不并入发送状态' },
                { key: 'filtered', label: '已过滤', value: total.filtered, filter: { status: -2 }, tone: 'tone-red', hint: '状态 -2，沿用后台既有规则' },
                { key: 'disabled', label: '禁用', value: total.disabled, filter: { status: -3 }, tone: 'tone-gray', hint: '状态 -3，旅程/群组/素材禁用' }
            ]
        },
        emptyTaskTitle() {
            if (this.context.runCount > 0) {
                return '旅程当天已执行，但没有生成任务'
            }
            return '当天没有查到旅程执行记录，也没有任务记录'
        },
        emptyTaskDescription() {
            if (this.context.runCount > 0) {
                return '执行次数为 ' + this.context.runCount + '，任务为 0 是查询结果，不代表发送完成或失败。请结合批次和进入用户核对。'
            }
            return '该提示区别于“已执行且任务为零”。请确认日期、旅程和批次是否正确。'
        },
        taskContextLabel() {
            const date = this.filters.date
            const qy = this.taskFilters.qyWeixinUserid
            const qyLabel = qy === this.unassignedQy ? '未分配企微' : (qy || '全部企微')
            return date + ' · ' + qyLabel + (this.taskFilters.reason ? ' · 原因筛选' : '')
        },
        taskQyLabel() {
            return this.taskFilters.qyWeixinUserid === this.unassignedQy ? '未分配企微' : this.taskFilters.qyWeixinUserid
        },
        taskReasonLabel() {
            return this.taskFilters.reason === '__UNKNOWN__' ? '未知原因（后台未记录）' : this.taskFilters.reason
        },
        taskStatusesLabel() {
            return '未完成状态：' + (this.taskFilters.statuses || []).join(' / ')
        },
        userDrawerTitle() {
            const labels = {
                entered: '当日进入用户',
                task: '当日有任务用户',
                noTask: '当日进入但未生成任务用户',
                all: '表内累计进入用户'
            }
            return labels[this.userScope] || '用户明细'
        },
        userContextLabel() {
            return this.filters.date + ' · ' + (this.filters.qyWeixinUserid ? this.qyFilterLabel : '全部企微') +
                (this.filters.routeRecordId ? ' · 批次 ' + this.shortBatchId(this.filters.routeRecordId) : '')
        }
    },
    beforeDestroy() {
        this.unbindTaskScroll()
    },
    mounted() {
        this.filters.routeId = parseInt(this.$route.params.id, 10) || null
        this.filters.routeName = this.$route.query.name || ''
        this.loadRouteOptions()
        this.loadQwUsers()
        this.loadDashboard()
    },
    methods: {
        loadRouteOptions() {
            this.$apiGet('lechun-cms/scrmRoute/getSopDashboardRouteOptions', {}).then(res => {
                this.routeOptions = res || []
                if (this.filters.routeId && !this.routeOptions.some(item => item.routeId === this.filters.routeId)) {
                    this.routeOptions.unshift({
                        routeId: this.filters.routeId,
                        routeName: this.filters.routeName || ('旅程 ' + this.filters.routeId),
                        statusLabel: '当前旅程'
                    })
                }
                const current = this.routeOptions.find(item => item.routeId === this.filters.routeId)
                if (current) {
                    this.filters.routeName = current.routeName
                }
            }).catch(() => {})
        },
        loadQwUsers() {
            this.$apiGet('lechun-cms/scrmCalendar/getQwUser', {}).then(res => {
                this.qwUsers = res || []
            }).catch(() => {})
        },
        onRouteChange(routeId) {
            const current = this.routeOptions.find(item => item.routeId === routeId)
            if (current) {
                this.filters.routeName = current.routeName
            }
            this.filters.routeRecordId = ''
            const routeResult = this.$router.replace({ name: 'routeSopDashboard', params: { id: this.filters.routeId }, query: Object.assign({}, this.$route.query, { name: this.filters.routeName }) })
            if (routeResult && routeResult.catch) {
                routeResult.catch(() => {})
            }
            this.loadDashboard()
        },
        baseParams() {
            const params = {
                routeId: this.filters.routeId,
                date: this.filters.date
            }
            if (this.filters.qyWeixinUserid) {
                params.qyWeixinUserid = this.filters.qyWeixinUserid
            }
            if (this.filters.routeRecordId) {
                params.routeRecordId = this.filters.routeRecordId
            }
            return params
        },
        loadDashboard() {
            if (!this.filters.routeId) {
                this.loadError = '未指定旅程'
                return
            }
            const seq = ++this.dashboardSeq
            this.loading = true
            this.loadError = ''
            this.$apiGet('lechun-cms/scrmRoute/getSopDashboard', this.baseParams()).then(res => {
                if (seq !== this.dashboardSeq) {
                    return
                }
                this.dashboard = res
                this.loading = false
                if (this.filters.routeRecordId && !(res.batches || []).some(item => item.routeRecordId === this.filters.routeRecordId)) {
                    this.filters.routeRecordId = ''
                }
            }).catch(err => {
                if (seq !== this.dashboardSeq) {
                    return
                }
                this.dashboard = null
                this.loading = false
                this.loadError = (err && err.message) || '看板数据加载失败'
            })
        },
        refresh() {
            this.loadDashboard()
            if (this.taskDrawer) {
                this.loadTasks()
            }
            if (this.userDrawer) {
                this.loadUsers()
            }
        },
        openTasks(filter) {
            this.captureDashboardScroll()
            this.savedTaskScroll = 0
            const next = filter || {}
            const nextStatuses = Array.isArray(next.statuses) ? next.statuses.slice() : []
            this.taskFilters = {
                statusGroup: next.statusGroup || '',
                status: nextStatuses.length ? null : (next.status === 0 || next.status ? next.status : null),
                statuses: nextStatuses,
                reason: next.reason || '',
                keyword: next.keyword || '',
                qyWeixinUserid: typeof next.qyWeixinUserid === 'undefined' ? (this.filters.qyWeixinUserid || '') : next.qyWeixinUserid,
                routeRecordId: this.filters.routeRecordId || ''
            }
            this.taskPage = 1
            this.taskDrawer = true
            this.loadTasks()
        },
        loadTasks() {
            if (!this.filters.routeId) {
                return
            }
            const params = {
                routeId: this.filters.routeId,
                date: this.filters.date,
                currentPage: this.taskPage,
                pageSize: this.taskPageSize
            }
            if (this.taskFilters.qyWeixinUserid) {
                params.qyWeixinUserid = this.taskFilters.qyWeixinUserid
            }
            if (this.taskFilters.routeRecordId) {
                params.routeRecordId = this.taskFilters.routeRecordId
            }
            if (this.taskFilters.statusGroup) {
                params.statusGroup = this.taskFilters.statusGroup
            }
            if (this.taskFilters.status !== null && this.taskFilters.status !== '') {
                params.status = this.taskFilters.status
            }
            if (this.taskFilters.statuses && this.taskFilters.statuses.length) {
                params.statuses = this.taskFilters.statuses.join(',')
            }
            if (this.taskFilters.reason) {
                params.reason = this.taskFilters.reason
            }
            if (this.taskFilters.keyword) {
                params.keyword = this.taskFilters.keyword
            }
            const seq = ++this.taskSeq
            this.taskLoading = true
            this.taskError = ''
            this.$apiGet('lechun-cms/scrmRoute/getSopDashboardTaskPage', params).then(res => {
                if (seq !== this.taskSeq) {
                    return
                }
                this.taskList = (res && res.list) || []
                this.taskTotal = (res && res.total) || 0
                this.taskLoading = false
                this.savedTaskScroll = 0
                this.bindTaskScroll()
            }).catch(err => {
                if (seq !== this.taskSeq) {
                    return
                }
                this.taskList = []
                this.taskTotal = 0
                this.taskLoading = false
                this.taskError = (err && err.message) || '任务明细加载失败'
            })
        },
        searchTasks() {
            if (this.taskFilters.status !== null && this.taskFilters.status !== '') {
                this.taskFilters.statuses = []
            }
            this.taskPage = 1
            this.loadTasks()
        },
        resetTaskFilters() {
            this.taskFilters.statusGroup = ''
            this.taskFilters.status = null
            this.taskFilters.statuses = []
            this.taskFilters.reason = ''
            this.taskFilters.keyword = ''
            this.taskFilters.qyWeixinUserid = this.filters.qyWeixinUserid || ''
            this.searchTasks()
        },
        clearTaskQy() {
            this.taskFilters.qyWeixinUserid = ''
            this.searchTasks()
        },
        clearTaskReason() {
            this.taskFilters.reason = ''
            this.taskFilters.statusGroup = ''
            this.searchTasks()
        },
        clearTaskStatuses() {
            this.taskFilters.statuses = []
            this.searchTasks()
        },
        clearTaskBatch() {
            this.taskFilters.routeRecordId = ''
            this.searchTasks()
        },
        handleTaskPage(page) {
            this.taskPage = page
            this.loadTasks()
        },
        openWecomTasks(row) {
            this.openTasks({ qyWeixinUserid: row.qyWeixinUserid })
        },
        openReasonTasks(row) {
            this.openTasks({ reason: row.reason, statusGroup: 'invalid' })
        },
        openUsers(scope) {
            this.captureDashboardScroll()
            this.userScope = scope || 'entered'
            this.userKeyword = ''
            this.userPage = 1
            this.userDrawer = true
            this.loadUsers()
        },
        onUserScopeChange() {
            this.userPage = 1
            this.loadUsers()
        },
        loadUsers() {
            if (!this.filters.routeId) {
                return
            }
            const params = {
                routeId: this.filters.routeId,
                date: this.filters.date,
                userScope: this.userScope,
                currentPage: this.userPage,
                pageSize: this.userPageSize
            }
            if (this.filters.qyWeixinUserid) {
                params.qyWeixinUserid = this.filters.qyWeixinUserid
            }
            if (this.filters.routeRecordId) {
                params.routeRecordId = this.filters.routeRecordId
            }
            if (this.userKeyword) {
                params.keyword = this.userKeyword
            }
            const seq = ++this.userSeq
            this.userLoading = true
            this.userError = ''
            this.$apiGet('lechun-cms/scrmRoute/getSopDashboardUserPage', params).then(res => {
                if (seq !== this.userSeq) {
                    return
                }
                this.userList = (res && res.list) || []
                this.userTotal = (res && res.total) || 0
                this.userLoading = false
            }).catch(err => {
                if (seq !== this.userSeq) {
                    return
                }
                this.userList = []
                this.userTotal = 0
                this.userLoading = false
                this.userError = (err && err.message) || '用户明细加载失败'
            })
        },
        searchUsers() {
            this.userPage = 1
            this.loadUsers()
        },
        handleUserPage(page) {
            this.userPage = page
            this.loadUsers()
        },
        openUserTasks(row) {
            this.openTasks({
                keyword: row.customerId || row.nickName,
                qyWeixinUserid: row.qyWeixinUserid || this.unassignedQy
            })
        },
        openTaskDetail(row) {
            this.captureTaskScroll()
            const seq = ++this.detailSeq
            this.detailDrawer = true
            this.detailLoading = true
            this.detailError = ''
            this.detail = null
            this.$apiGet('lechun-cms/scrmRoute/getSopDashboardTaskDetail', { taskId: row.id }).then(res => {
                if (seq !== this.detailSeq || !this.detailDrawer) {
                    return
                }
                this.detail = res
                this.detailLoading = false
            }).catch(err => {
                if (seq !== this.detailSeq || !this.detailDrawer) {
                    return
                }
                this.detailLoading = false
                this.detailError = (err && err.message) || '任务详情加载失败'
            })
        },
        invalidateDetailRequest() {
            this.detailSeq++
            this.detailLoading = false
        },
        captureDashboardScroll() {
            const element = document.querySelector('.sop-dashboard .dashboard-scroll')
            this.savedDashboardScroll = element ? element.scrollTop : 0
        },
        restoreDashboardScroll() {
            const self = this
            this.$nextTick(function () {
                setTimeout(function () {
                    const element = document.querySelector('.sop-dashboard .dashboard-scroll')
                    if (element) {
                        element.scrollTop = self.savedDashboardScroll
                    }
                }, 0)
            })
        },
        taskScrollWrappers() {
            const table = this.$refs.taskTable
            if (!table || !table.$el) {
                return []
            }
            return Array.prototype.slice.call(table.$el.querySelectorAll('.el-table__body-wrapper'))
        },
        bindTaskScroll() {
            if (!this.taskDrawer) {
                return
            }
            const self = this
            this.$nextTick(function () {
                const wrappers = self.taskScrollWrappers()
                if (!wrappers.length) {
                    return
                }
                self.unbindTaskScroll()
                self.taskScrollElements = wrappers
                wrappers.forEach(function (element) {
                    element.addEventListener('scroll', self.onTaskScroll, { passive: true })
                })
                self.onTaskScroll()
            })
        },
        unbindTaskScroll() {
            if (this.taskScrollElements && this.taskScrollElements.length) {
                this.taskScrollElements.forEach(element => {
                    element.removeEventListener('scroll', this.onTaskScroll)
                })
            }
            this.taskScrollElements = []
        },
        rememberTaskScroll() {
            this.onTaskScroll()
        },
        onTaskScroll() {
            let maxScroll = 0
            const wrappers = this.taskScrollElements && this.taskScrollElements.length ? this.taskScrollElements : this.taskScrollWrappers()
            wrappers.forEach(element => {
                maxScroll = Math.max(maxScroll, element.scrollTop || 0)
            })
            if (maxScroll > 0) {
                this.savedTaskScroll = maxScroll
            }
        },
        onTaskDrawerClosed() {
            this.taskSeq++
            this.unbindTaskScroll()
            this.restoreDashboardScroll()
        },
        captureTaskScroll() {
            this.onTaskScroll()
        },
        restoreTaskScroll() {
            const self = this
            this.$nextTick(function () {
                setTimeout(function () {
                    const wrappers = self.taskScrollWrappers()
                    wrappers.forEach(element => {
                        element.scrollTop = self.savedTaskScroll
                    })
                }, 50)
            })
        },
        formatNumber(value) {
            if (value === null || value === undefined || value === '') {
                return '—'
            }
            const number = Number(value)
            return isNaN(number) ? String(value) : number.toLocaleString('zh-CN')
        },
        formatPercent(value) {
            if (value === null || value === undefined) {
                return '—'
            }
            return (Number(value) * 100).toFixed(1) + '%'
        },
        statusTagType(status) {
            if (status === 0) return 'warning'
            if (status === 1) return ''
            if (status === 2 || status === 3) return 'success'
            if (status === -1) return 'info'
            if (status === -2 || status === -3) return 'danger'
            return 'info'
        },
        timelineColor(state) {
            if (state === 'confirmed') return '#67c23a'
            if (state === 'invalid') return '#f56c6c'
            if (state === 'pending') return '#e6a23c'
            return '#909399'
        },
        timelineTagType(state) {
            if (state === 'confirmed') return 'success'
            if (state === 'invalid') return 'danger'
            if (state === 'pending') return 'warning'
            return 'info'
        },
        timelineStateLabel(state) {
            const labels = { confirmed: '已确认', invalid: '无效', pending: '待确认', missing: '缺失', unknown: '未确认' }
            return labels[state] || '未确认'
        },
        shortBatchId(id) {
            if (!id) return '—'
            return id.length > 10 ? id.slice(0, 8) + '…' : id
        },
        batchLabel(item) {
            return (item.executeTime || item.executeDate || this.shortBatchId(item.routeRecordId)) +
                ' · 任务记录 ' + (item.rawTaskRows || 0)
        }
    }
}
</script>

<style lang="scss" rel="stylesheet/scss">
.sop-dashboard {
    height: 100%;
    display: flex;
    flex-direction: column;

    .filter-bar {
        background: #f7f9fa;
        border: 1px solid #ebeef5;
        border-radius: 4px;
        padding: 10px 12px 0;
        flex-shrink: 0;
    }
    .dashboard-scroll {
        flex: 1;
        overflow: auto;
        padding: 12px 2px 20px;
    }
    .dashboard-content {
        min-height: 220px;
    }
    .alert-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
    }
    .context-card {
        background: #f5f7fa;
        border: 1px solid #ebeef5;
        border-radius: 4px;
        padding: 10px 12px;
    }
    .context-main {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
    }
    .context-route {
        font-size: 15px;
        font-weight: 600;
    }
    .context-date {
        color: #606266;
    }
    .context-tag {
        font-size: 12px;
        color: #2dacd1;
        background: #ecf7fa;
        border-radius: 3px;
        padding: 1px 6px;
    }
    .context-meta {
        margin-top: 7px;
        display: flex;
        flex-wrap: wrap;
        gap: 4px 18px;
        color: #909399;
        font-size: 12px;
    }
    .section-title {
        margin: 16px 0 8px;
        font-size: 14px;
        font-weight: 600;
        color: #303133;
    }
    .section-hint {
        margin-left: 8px;
        font-size: 12px;
        font-weight: normal;
        color: #909399;
    }
    .kpi-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(145px, 1fr));
        gap: 8px;
    }
    .kpi-card {
        border: 1px solid #ebeef5;
        border-radius: 4px;
        padding: 9px 11px;
        cursor: pointer;
        background: #fff;
        transition: box-shadow .15s;
        &:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, .1);
        }
        .kpi-label {
            color: #606266;
            font-size: 12px;
        }
        .kpi-value {
            font-size: 22px;
            font-weight: 600;
            margin: 3px 0;
            color: #303133;
        }
        .kpi-hint {
            color: #a0a4ab;
            font-size: 11px;
            line-height: 15px;
        }
    }
    .tone-green { border-left: 3px solid #67c23a; }
    .tone-red { border-left: 3px solid #f56c6c; }
    .tone-orange { border-left: 3px solid #e6a23c; }
    .tone-blue { border-left: 3px solid #409eff; }
    .tone-gray { border-left: 3px solid #909399; }
    .empty-notice {
        margin-top: 10px;
    }
    .panel-grid {
        display: grid;
        grid-template-columns: minmax(0, 1.7fr) minmax(330px, 1fr);
        gap: 12px;
        margin-top: 16px;
    }
    .panel {
        border: 1px solid #ebeef5;
        border-radius: 4px;
        padding: 10px;
        min-width: 0;
    }
    .panel-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 8px;
    }
    .panel-title {
        font-size: 14px;
        font-weight: 600;
    }
    .panel-actions {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .panel-footnote {
        margin-top: 8px;
        color: #909399;
        font-size: 12px;
        line-height: 18px;
    }
    .clickable-table {
        cursor: pointer;
    }
    .cell-link {
        color: #2dacd1;
        cursor: pointer;
        &:hover { text-decoration: underline; }
        &.danger { color: #f56c6c; }
    }
    .wecom-name {
        font-weight: 600;
    }
    .wecom-id {
        color: #a0a4ab;
        font-size: 11px;
    }
    .unknown-reason {
        color: #e6a23c;
        font-weight: 600;
    }
    .reason-note {
        color: #a0a4ab;
        font-size: 11px;
    }
    .option-extra {
        float: right;
        color: #a0a4ab;
        font-size: 12px;
    }
    .drawer-wrap {
        padding: 8px 18px 0;
    }
    .drawer-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
        padding: 8px 0 10px;
        border-bottom: 1px solid #ebeef5;
    }
    .drawer-head > div:first-child {
        flex: 1;
        min-width: 0;
    }
    .drawer-title {
        font-size: 16px;
        font-weight: 600;
        margin-right: 12px;
    }
    .drawer-filter {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        padding: 10px 0;
    }
    .drawer-table {
        padding-bottom: 10px;
    }
    .strong {
        font-weight: 600;
    }
    .muted {
        color: #909399;
        font-size: 11px;
    }
    .status-reason {
        max-width: 260px;
        white-space: normal;
        word-break: break-all;
        font-size: 12px;
    }
    .status-count {
        display: inline-block;
        min-width: 18px;
        text-align: center;
    }
    .detail-wrap {
        padding: 0 20px 20px;
    }
    .detail-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
        gap: 9px 20px;
        font-size: 13px;
        padding: 12px;
        background: #f5f7fa;
        border-radius: 4px;
        line-height: 20px;
        word-break: break-all;
    }
    .detail-grid > div {
        min-width: 0;
    }
    .detail-label {
        display: inline-block;
        min-width: 62px;
        color: #909399;
        margin-right: 6px;
    }
    .reason-list {
        margin-top: 10px;
        font-size: 13px;
    }
    .reason-tag {
        margin: 0 4px 4px 0;
    }
    .detail-section-title {
        margin: 16px 0 8px;
        font-weight: 600;
        font-size: 14px;
    }
    .detail-timeline {
        padding-left: 2px;
    }
    .timeline-title {
        font-weight: 600;
        margin-bottom: 3px;
    }
    .timeline-detail {
        color: #606266;
        font-size: 12px;
        line-height: 18px;
        white-space: pre-wrap;
        word-break: break-all;
    }
    .timeline-evidence {
        color: #a0a4ab;
        font-size: 11px;
        margin-top: 2px;
    }
    @media (max-width: 1200px) {
        .panel-grid {
            grid-template-columns: 1fr;
        }
    }
}
</style>
